import moment from "moment";
import { Page, Text, View, Document, StyleSheet, Image, pdf, Link } from "@react-pdf/renderer";
import { JobInterview } from "src/types";
import Logo from "src/assets/images/general/logo.png";
import VideoIcon from "src/assets/images/generic/video-play.png";
import { dateFormat1 } from "src/utils/dates/formats";
import { ReactComponent as DownloadIcon } from "src/assets/svg/download.svg";

import Button from "src/components/Button";
import { useState } from "react";
import { customToast } from "src/utils/toast";
import { Feedback, FeedbackData, SkillsFeedback } from "src/classes/models/interview";
import { JobInterviewClass } from "src/classes/models/job_interview";

// Flag to test without downloading
const dev_mode = false;
let test_url = "";

const DownloadReport = ({
  interview,
  feedback
}: {
  interview: JobInterview;
  feedback?: Feedback;
}) => {
  const [generating, setGenerating] = useState(false);
  if (!feedback) return null;
  const { first_name, last_name, Interview } = interview;
  const { date_completed } = Interview;

  //  IMPORTANT: We explicitly generate the pdf when the button is clicked because there is a chance that
  // react-pdf freezes the browser tab, so this way it only happens when they click the button and not
  // when they enter the page that uses the button
  const onDownload = async () => {
    setGenerating(true);
    try {
      const date = date_completed ? "_" + moment(date_completed).utc().format("MM_DD_YY") : "";
      const blob = await pdf(<ReportPdf interview={interview} feedback={feedback} />).toBlob();
      const url = URL.createObjectURL(blob);
      test_url = url;
      const link = document.createElement("a");
      link.href = url;
      link.download = `${first_name}_${last_name}_Outdefine_Interview${date}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      customToast({ message: "Error occured on pdf generation" });
    }
    setGenerating(false);
  };

  return (
    <>
      <Button className="!relative" shapeVariant={2} loading={generating} onClick={onDownload}>
        <DownloadIcon className="w-6 h-6 mr-2 stroke-white" />
        Download Report
        {dev_mode && (
          <iframe
            key={test_url}
            className="absolute -left-[700px] top-[100px] w-[800px] h-[500px]  border-[1px] border-odfblue"
            src={test_url}
          />
        )}
      </Button>
    </>
  );
};

export default DownloadReport;

// === COMPONENTS FOR PDF USE ONLY ===

const ReportPdf = ({ interview, feedback }: { interview: JobInterview; feedback: Feedback }) => {
  const { email, first_name, last_name, Job, Interview } = interview;

  const { overall, communication, work_experience, skills } = feedback;
  const { date_completed } = Interview;
  const _Interview = new JobInterviewClass(interview);
  if (!feedback) return null;
  const recordingUrl = window.location.href.split("tab=")[0] + "tab=Recording";

  return (
    <Document>
      <Page size="A4">
        {/* HEADER */}
        <View
          style={{
            backgroundColor: "#F9F9F9",
            padding: 24,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderBottomWidth: 1,
            borderBottomColor: "#CCC"
          }}
        >
          <Image src={Logo} style={{ width: 138, height: 24, marginBottom: 32 }} />
          <Text style={styles.header}>
            <Text style={styles.blueText}>
              {first_name} {last_name}&apos;s
            </Text>{" "}
            Interview report {"\n"}
            for <Text style={styles.blueText}>{Job.job_title}</Text> role
          </Text>
        </View>
        {/* SUMMARY */}
        <View style={{ width: "100%", padding: 36 }}>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between"
            }}
          >
            <SummaryColumn>
              <SummaryText title="Email Address" content={email} />
              <SummaryText
                title="Interview Duration"
                content={_Interview.interviewDurationText()}
              />
            </SummaryColumn>
            <SummaryColumn>
              <SummaryText title="Job Role" content={Job.job_title} />
              {date_completed && (
                <SummaryText title="Date of interview" content={dateFormat1(date_completed)} />
              )}
            </SummaryColumn>
          </View>

          <View
            style={{
              width: "100%",
              backgroundColor: "#F9F9F9",
              padding: 24,
              borderRadius: 14,
              flexDirection: "row",
              gap: 16,
              alignItems: "center",
              marginTop: 32
            }}
          >
            <Image src={VideoIcon} style={{ width: 36, height: 36 }} />
            <View>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: "Helvetica-Bold"
                }}
              >
                Transcript and Video Recordings:{"\n"}
              </Text>

              <Text
                style={{
                  lineHeight: 1.4,
                  marginTop: 8,
                  fontSize: 14,
                  fontFamily: "Helvetica-Bold"
                }}
              >
                {/* Link is atomic, we need to separate the text if we want multiple lines */}
                <Link src={recordingUrl}>{recordingUrl.slice(0, 45)}</Link>
                {"\n"}
                <Link src={recordingUrl}>{recordingUrl.slice(45)}</Link>
              </Text>
            </View>
          </View>
          <View
            style={{
              width: "100%",
              borderWidth: 1,
              borderColor: "#5F5FFF",
              padding: 24,
              borderRadius: 16,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 32
            }}
          >
            <Text
              style={{
                fontSize: 18,
                fontFamily: "Helvetica-Bold"
              }}
            >
              Overall Hire Score
            </Text>
            <Text
              style={{
                fontSize: 31,
                fontFamily: "Helvetica-Bold",
                color: overall._score.style.color
              }}
            >
              {overall.score === "Incomplete" ? "N/A" : overall.score}
            </Text>
          </View>
        </View>
      </Page>
      {/* CONTENT */}
      <Page wrap={false} size="A4">
        <View style={{ backgroundColor: "#F9F9F9", padding: 36, flexDirection: "column", gap: 36 }}>
          <FeedbackCard feedback={work_experience} />
          {!!skills && <FeedbackCard feedback={skills} />}
          <FeedbackCard feedback={communication} />
        </View>
      </Page>

      <Page wrap={false} size="A4">
        <View style={{ backgroundColor: "#F9F9F9", padding: 36, flexDirection: "column", gap: 36 }}>
          <SkillsFeedbackCards feedback={skills} />
        </View>

        {/* FOOTER */}
        <View
          style={{
            padding: 36,
            backgroundColor: "#F9F9F9",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 18
          }}
        >
          <Text
            style={{
              fontSize: 18,
              fontFamily: "Helvetica-Bold"
            }}
          >
            interviewed by Debbie, the AI Recruiter by
          </Text>
          <Image src={Logo} style={{ width: 138, height: 24 }} />
        </View>
      </Page>
    </Document>
  );
};

const ScoreBadge = ({ feedback }: { feedback: FeedbackData }) => (
  <Text
    style={{
      fontSize: 18,
      fontFamily: "Helvetica-Bold",
      padding: 8,
      borderRadius: 8,
      borderWidth: 1,
      color: feedback._score.style.color,
      borderColor: feedback._score.style.color
    }}
  >
    {feedback.score}
  </Text>
);

const SummaryColumn = ({ children }) => (
  <View style={{ flexDirection: "column", gap: 24 }}>{children}</View>
);

const SummaryText = ({ title = "", content = "" }) => (
  <View style={{}}>
    <Text style={{ color: "#8A8A8A", fontFamily: "Helvetica", fontSize: 12 }}>{title}</Text>
    <Text style={{ marginTop: 8, fontFamily: "Helvetica-Bold", fontSize: 14 }}>{content}</Text>
  </View>
);

const FeedbackWrapper = ({ feedback, children }: { feedback: FeedbackData; children }) => (
  <View style={{ width: "100%", backgroundColor: "white", overflow: "hidden", borderRadius: 8 }}>
    <View
      style={{
        borderLeftColor: "#5F5FFF",
        borderLeftWidth: 3,
        padding: 20,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottomColor: "#CCC",
        borderBottomWidth: 1
      }}
    >
      <Text style={{ fontSize: 18, fontFamily: "Helvetica-Bold" }}>{feedback.title}</Text>
      <ScoreBadge feedback={feedback} />
    </View>
    {children}
  </View>
);

const FeedbackCard = ({ feedback }: { feedback: FeedbackData }) => (
  <FeedbackWrapper feedback={feedback}>
    <Text style={{ padding: 14, fontFamily: "Helvetica", fontSize: 14, lineHeight: 2 }}>
      {feedback.summary}
    </Text>
  </FeedbackWrapper>
);

const SkillsFeedbackCards = ({ feedback }: { feedback: SkillsFeedback }) => {
  console.log(feedback);
  if (!feedback) return null;
  const { technical, intercultural } = feedback;
  const [oneInter, oneTech] = [!!intercultural?.skills.length, !!technical?.skills.length];
  if (!oneInter && !oneTech) return null;

  const Title = ({ children }) => (
    <Text style={{ fontSize: 16, fontFamily: "Helvetica-Bold", color: "#5F5FFF", lineHeight: 3 }}>
      {children}
    </Text>
  );

  const Skill = ({ feedback }: { feedback: FeedbackData }) => (
    <View
      style={{
        backgroundColor: "#F9F9F9",
        borderRadius: 8,
        padding: 16,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16
      }}
    >
      <View style={{ maxWidth: 270 }}>
        <Text style={{ fontSize: 16, fontFamily: "Helvetica-Bold" }}>{feedback.title}</Text>
        <Text style={{ fontSize: 14, fontFamily: "Helvetica", marginTop: 12, lineHeight: 1.6 }}>
          {feedback.summary}
        </Text>
      </View>
      <ScoreBadge feedback={feedback} />
    </View>
  );

  return (
    <FeedbackWrapper feedback={feedback}>
      {oneTech && (
        <View style={{ padding: 24 }}>
          <Title>Technical Skills</Title>
          {technical?.skills.map((t) => <Skill key={t.title} feedback={t} />)}
        </View>
      )}
      {oneInter && (
        <View style={{ padding: 24 }}>
          <Title>Intercultural Skills</Title>
          {intercultural?.skills.map((t) => <Skill key={t.title} feedback={t} />)}
        </View>
      )}
    </FeedbackWrapper>
  );
};

// Create styles
const styles = StyleSheet.create({
  page: {},
  header: {
    textAlign: "center",
    fontSize: 20,
    fontFamily: "Helvetica-Bold"
  },
  summarySection: {
    backgroundColor: "white",
    padding: 36
  },
  blueText: {
    color: "#5F5FFF",
    padding: 36
  }
});
